import { makeAutoObservable } from 'mobx';

import type { NID, SID } from '@navina/api-types';

/**
 * Tracing of the patient portrait a provider currently has open, for features that must react when
 * it closes (releasing a live Ambient recording, flushing drafts, ending a timer).
 *
 * The portrait is the overall page, which is why nothing here is named after the summary: a summary
 * (`ClinicalSummary`, `DxSummary`) is one tab inside it, and the portrait stays open while the
 * provider moves between those tabs.
 *
 * Features call `tracePatientPortraitLifecycle` and get told; the infra that knows about routing
 * calls `reportPatientPortraitOpened` / `reportNoPatientPortraitOpen`. That direction is the whole
 * point - the route tracer must never need to import a feature's store to tear it down.
 *
 * ```ts
 * const patientPortraitLifecycleModel = getOrCreatePatientPortraitLifecycleModel();
 * const { maybeOpenPatientPortrait } = patientPortraitLifecycleModel;
 *
 * this.stopTracing = patientPortraitLifecycleModel.tracePatientPortraitLifecycle({
 * 	onPatientPortraitClosed: ({ closedPatientPortrait, closeReason }) => { ... },
 * });
 * ```
 *
 * Subscribing tells you about the transitions from that moment on and never replays the current
 * state, so read `maybeOpenPatientPortrait` for where the provider is right now.
 */

/**
 * A patient portrait is identified by the patient and/or the appointment it was opened for, never by
 * its URL. The same portrait stays open while the provider moves between its tabs and overlays, and
 * both the appointment-based (`/:navinaAppointmentId`) and patient-based
 * (`/patient/:navinaPatientId/appointment/:navinaAppointmentId`) page families describe the same
 * portrait through different pathnames.
 */
export interface PatientPortraitIdentity {
	readonly maybeNavinaPatientId: NID | null;
	readonly maybeNavinaAppointmentId: SID | null;
}

export enum PatientPortraitCloseReason {
	NavigatedAwayFromPatientPortrait = 'NavigatedAwayFromPatientPortrait',
	SwitchedToAnotherPatientPortrait = 'SwitchedToAnotherPatientPortrait',
}

export interface PatientPortraitOpenedEvent {
	readonly openedPatientPortrait: PatientPortraitIdentity;
}

export interface PatientPortraitClosedEvent {
	readonly closedPatientPortrait: PatientPortraitIdentity;
	readonly closeReason: PatientPortraitCloseReason;
	readonly maybeNextOpenPatientPortrait: PatientPortraitIdentity | null;
}

type PatientPortraitOpenedListener = (event: PatientPortraitOpenedEvent) => void;
type PatientPortraitClosedListener = (event: PatientPortraitClosedEvent) => void;

export type StopTracingPatientPortraitLifecycle = () => void;

export interface TracePatientPortraitLifecycleArgs {
	readonly onPatientPortraitOpened?: PatientPortraitOpenedListener;
	readonly onPatientPortraitClosed?: PatientPortraitClosedListener;
}

interface CloseOpenPatientPortraitArgs {
	readonly closeReason: PatientPortraitCloseReason;
	readonly maybeNextOpenPatientPortrait: PatientPortraitIdentity | null;
}

export function isSamePatientPortrait(
	patientPortrait: PatientPortraitIdentity,
	otherPatientPortrait: PatientPortraitIdentity,
): boolean {
	return (
		patientPortrait.maybeNavinaPatientId === otherPatientPortrait.maybeNavinaPatientId &&
		patientPortrait.maybeNavinaAppointmentId === otherPatientPortrait.maybeNavinaAppointmentId
	);
}

// This model is a singleton with no lifetime of its own, so a subscriber that never calls its stop
// function leaks for the rest of the browser tab. Mirrors Node's EventEmitter max-listeners warning
// to surface that class of bug during development instead of letting it accumulate silently.
//
// Deliberately low: a handful of legitimate consumers is the expected steady state, so anything past
// this is far more likely to be a per-mount subscriber accumulating than real demand. A lax
// threshold would let a slow leak run a whole dev session without ever tripping.
export const LISTENER_LEAK_WARNING_THRESHOLD = 5;

type PatientPortraitListenerName = 'onPatientPortraitOpened' | 'onPatientPortraitClosed';

interface WarnIfListenerCountMightBeLeakingArgs {
	readonly listenerCount: number;
	readonly listenerName: PatientPortraitListenerName;
}

function warnIfListenerCountMightBeLeaking({
	listenerCount,
	listenerName,
}: WarnIfListenerCountMightBeLeakingArgs): void {
	if (!import.meta.env.DEV || listenerCount !== LISTENER_LEAK_WARNING_THRESHOLD + 1) {
		return;
	}

	console.warn(
		`PatientPortraitLifecycleModel now has ${listenerCount} "${listenerName}" listeners registered. This singleton ` +
			'lives for the whole browser session, so a subscriber that never calls the stop function returned by ' +
			'tracePatientPortraitLifecycle leaks for the rest of the tab - check every caller disposes its subscription ' +
			'(see the trace-patient-portrait-lifecycle skill).',
	);
}

function warnAboutAlreadyRegisteredListener(listenerName: PatientPortraitListenerName): void {
	if (!import.meta.env.DEV) {
		return;
	}

	console.warn(
		`This "${listenerName}" listener is already registered on PatientPortraitLifecycleModel, so the duplicate was ` +
			'ignored. A store that subscribes twice usually means its initializer ran again without its stop ' +
			'function being called first - guard the second call, or stop tracing in dispose ' +
			'(see the trace-patient-portrait-lifecycle skill).',
	);
}

export interface IPatientPortraitLifecycleModel {
	readonly maybeOpenPatientPortrait: PatientPortraitIdentity | null;
	readonly isPatientPortraitOpen: boolean;
	readonly tracePatientPortraitLifecycle: (
		args: TracePatientPortraitLifecycleArgs,
	) => StopTracingPatientPortraitLifecycle;
	/**
	 * Fires for the next close only, for any `PatientPortraitCloseReason` - leaving to another product
	 * and switching to another patient/appointment both count. Auto-unsubscribes after firing once,
	 * so there is nothing to dispose if it fires; call the returned stop function only to cancel
	 * before that happens (e.g. the caller itself unmounts first).
	 */
	readonly notifyOncePatientPortraitClosed: (
		onPatientPortraitClosed: PatientPortraitClosedListener,
	) => StopTracingPatientPortraitLifecycle;
	/** Infra only - called by `tracePatientPortraitsOnRouter`, not by features. */
	readonly reportPatientPortraitOpened: (openedPatientPortrait: PatientPortraitIdentity) => void;
	/** Infra only - called by `tracePatientPortraitsOnRouter`, not by features. */
	readonly reportNoPatientPortraitOpen: () => void;
}

export class PatientPortraitLifecycleModel implements IPatientPortraitLifecycleModel {
	maybeOpenPatientPortrait: PatientPortraitIdentity | null = null;

	// Kept out of MobX: an observable array wraps the functions it holds, and a listener that no
	// longer compares equal to the one that was handed in can never be removed again. They are not
	// `private` only because MobX's annotation map cannot name a private member; neither is part of
	// `IPatientPortraitLifecycleModel`, which is what features receive.
	patientPortraitOpenedListeners: ReadonlyArray<PatientPortraitOpenedListener> = [];
	patientPortraitClosedListeners: ReadonlyArray<PatientPortraitClosedListener> = [];

	private constructor() {
		makeAutoObservable(this, {
			patientPortraitOpenedListeners: false,
			patientPortraitClosedListeners: false,
		});
	}

	static readonly create = (): PatientPortraitLifecycleModel => new PatientPortraitLifecycleModel();

	get isPatientPortraitOpen(): boolean {
		return this.maybeOpenPatientPortrait !== null;
	}

	private readonly setMaybeOpenPatientPortrait = (maybeOpenPatientPortrait: PatientPortraitIdentity | null): void => {
		this.maybeOpenPatientPortrait = maybeOpenPatientPortrait;
	};

	private readonly setPatientPortraitOpenedListeners = (
		patientPortraitOpenedListeners: typeof this.patientPortraitOpenedListeners,
	): void => {
		this.patientPortraitOpenedListeners = patientPortraitOpenedListeners;
	};

	private readonly setPatientPortraitClosedListeners = (
		patientPortraitClosedListeners: typeof this.patientPortraitClosedListeners,
	): void => {
		this.patientPortraitClosedListeners = patientPortraitClosedListeners;
	};

	// Registering the same listener twice is treated as registering it once: the second call cannot
	// hand back a second stop function anyway, so stacking it would orphan a listener that nothing
	// can ever remove.
	private readonly addPatientPortraitOpenedListener = (listener: PatientPortraitOpenedListener): void => {
		if (this.patientPortraitOpenedListeners.includes(listener)) {
			warnAboutAlreadyRegisteredListener('onPatientPortraitOpened');

			return;
		}

		this.setPatientPortraitOpenedListeners([...this.patientPortraitOpenedListeners, listener]);
		warnIfListenerCountMightBeLeaking({
			listenerCount: this.patientPortraitOpenedListeners.length,
			listenerName: 'onPatientPortraitOpened',
		});
	};

	private readonly addPatientPortraitClosedListener = (listener: PatientPortraitClosedListener): void => {
		if (this.patientPortraitClosedListeners.includes(listener)) {
			warnAboutAlreadyRegisteredListener('onPatientPortraitClosed');

			return;
		}

		this.setPatientPortraitClosedListeners([...this.patientPortraitClosedListeners, listener]);
		warnIfListenerCountMightBeLeaking({
			listenerCount: this.patientPortraitClosedListeners.length,
			listenerName: 'onPatientPortraitClosed',
		});
	};

	readonly tracePatientPortraitLifecycle = ({
		onPatientPortraitOpened,
		onPatientPortraitClosed,
	}: TracePatientPortraitLifecycleArgs): StopTracingPatientPortraitLifecycle => {
		if (onPatientPortraitOpened) {
			this.addPatientPortraitOpenedListener(onPatientPortraitOpened);
		}

		if (onPatientPortraitClosed) {
			this.addPatientPortraitClosedListener(onPatientPortraitClosed);
		}

		const stopTracingPatientPortraitLifecycle = (): void => {
			this.setPatientPortraitOpenedListeners(
				this.patientPortraitOpenedListeners.filter(function keepOtherOpenedListeners(listener): boolean {
					return listener !== onPatientPortraitOpened;
				}),
			);

			this.setPatientPortraitClosedListeners(
				this.patientPortraitClosedListeners.filter(function keepOtherClosedListeners(listener): boolean {
					return listener !== onPatientPortraitClosed;
				}),
			);
		};

		return stopTracingPatientPortraitLifecycle;
	};

	readonly notifyOncePatientPortraitClosed = (
		onPatientPortraitClosed: PatientPortraitClosedListener,
	): StopTracingPatientPortraitLifecycle => {
		const stopTracingOnce = this.tracePatientPortraitLifecycle({
			onPatientPortraitClosed: (event): void => {
				stopTracingOnce();
				onPatientPortraitClosed(event);
			},
		});

		return stopTracingOnce;
	};

	readonly reportPatientPortraitOpened = (openedPatientPortrait: PatientPortraitIdentity): void => {
		const { maybeOpenPatientPortrait } = this;

		if (maybeOpenPatientPortrait && isSamePatientPortrait(maybeOpenPatientPortrait, openedPatientPortrait)) {
			return;
		}

		this.closeOpenPatientPortrait({
			closeReason: PatientPortraitCloseReason.SwitchedToAnotherPatientPortrait,
			maybeNextOpenPatientPortrait: openedPatientPortrait,
		});

		this.setMaybeOpenPatientPortrait(openedPatientPortrait);
		this.notifyPatientPortraitOpened({ openedPatientPortrait });
	};

	readonly reportNoPatientPortraitOpen = (): void => {
		this.closeOpenPatientPortrait({
			closeReason: PatientPortraitCloseReason.NavigatedAwayFromPatientPortrait,
			maybeNextOpenPatientPortrait: null,
		});
	};

	private readonly closeOpenPatientPortrait = ({
		closeReason,
		maybeNextOpenPatientPortrait,
	}: CloseOpenPatientPortraitArgs): void => {
		const closedPatientPortrait = this.maybeOpenPatientPortrait;

		if (!closedPatientPortrait) {
			return;
		}

		this.setMaybeOpenPatientPortrait(null);
		this.notifyPatientPortraitClosed({ closedPatientPortrait, closeReason, maybeNextOpenPatientPortrait });
	};

	// One feature throwing while it tears itself down must not take navigation, or the other
	// listeners, down with it - every listener is called in isolation.
	private readonly notifyPatientPortraitOpened = (event: PatientPortraitOpenedEvent): void => {
		this.patientPortraitOpenedListeners.forEach(function callPatientPortraitOpenedListener(listener): void {
			try {
				listener(event);
			} catch (error) {
				console.error('A patient portrait opened listener has thrown', { error });
			}
		});
	};

	private readonly notifyPatientPortraitClosed = (event: PatientPortraitClosedEvent): void => {
		this.patientPortraitClosedListeners.forEach(function callPatientPortraitClosedListener(listener): void {
			try {
				listener(event);
			} catch (error) {
				console.error('A patient portrait closed listener has thrown', { error });
			}
		});
	};
}

let maybePatientPortraitLifecycleModel: PatientPortraitLifecycleModel | null = null;

export function getOrCreatePatientPortraitLifecycleModel(): IPatientPortraitLifecycleModel {
	maybePatientPortraitLifecycleModel ??= PatientPortraitLifecycleModel.create();

	return maybePatientPortraitLifecycleModel;
}
